// ── SpotifyConnect.js — real Spotify OAuth via PKCE ───────────────────────
// Flow:
//   1. User clicks Connect → redirected to Spotify auth page
//   2. Spotify redirects back to /#spotify/callback?code=...
//   3. App.js detects hash, calls handleSpotifyCallback()
//   4. Exchange code for access token (server-side via Edge Function)
//   5. Fetch top artists with access token
//   6. Save to user_profiles.spotify_data in Supabase
//
// Requires env vars:
//   REACT_APP_SPOTIFY_CLIENT_ID — from Spotify Developer Dashboard
//   Redirect URI in Spotify Dashboard: https://beelove.me/#spotify/callback

import { useState, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';

const SPOTIFY_CLIENT_ID  = process.env.REACT_APP_SPOTIFY_CLIENT_ID || '';
const SPOTIFY_REDIRECT   = `${window.location.origin}/#spotify/callback`;
const SPOTIFY_SCOPES     = 'user-top-read user-read-private';

// ── PKCE helpers ─────────────────────────────────────────────────────────
function base64url(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generatePKCE() {
  const verifier  = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  );
  return { verifier, challenge };
}

// ── Initiate OAuth flow ───────────────────────────────────────────────────
export async function initiateSpotifyOAuth() {
  if (!SPOTIFY_CLIENT_ID) {
    console.error('REACT_APP_SPOTIFY_CLIENT_ID not set');
    return { error: 'Spotify not configured' };
  }
  const { verifier, challenge } = await generatePKCE();
  const state = crypto.randomUUID();
  // Store verifier in sessionStorage for callback
  sessionStorage.setItem('spotify_pkce_verifier', verifier);
  sessionStorage.setItem('spotify_oauth_state',   state);

  const params = new URLSearchParams({
    client_id:             SPOTIFY_CLIENT_ID,
    response_type:         'code',
    redirect_uri:          SPOTIFY_REDIRECT,
    scope:                 SPOTIFY_SCOPES,
    state,
    code_challenge_method: 'S256',
    code_challenge:        challenge,
  });
  window.location.href = `https://accounts.spotify.com/authorize?${params}`;
  return { error: null };
}

// ── Handle OAuth callback (called from App.js when hash = #spotify/callback) ──
export async function handleSpotifyCallback(searchStr) {
  const params   = new URLSearchParams(searchStr);
  const code     = params.get('code');
  const state    = params.get('state');
  const error    = params.get('error');

  if (error) return { error };

  const savedState   = sessionStorage.getItem('spotify_oauth_state');
  const verifier     = sessionStorage.getItem('spotify_pkce_verifier');
  sessionStorage.removeItem('spotify_oauth_state');
  sessionStorage.removeItem('spotify_pkce_verifier');

  if (state !== savedState) return { error: 'State mismatch' };
  if (!code || !verifier)   return { error: 'Missing code or verifier' };

  // Exchange code for token
  const tokenRes = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'authorization_code',
      code,
      redirect_uri:  SPOTIFY_REDIRECT,
      client_id:     SPOTIFY_CLIENT_ID,
      code_verifier: verifier,
    }),
  });
  const tokenData = await tokenRes.json();
  if (!tokenData.access_token) return { error: tokenData.error || 'Token exchange failed' };

  // Fetch top artists
  const artistsRes = await fetch(
    'https://api.spotify.com/v1/me/top/artists?limit=10&time_range=medium_term',
    { headers: { Authorization: `Bearer ${tokenData.access_token}` } }
  );
  const artistsData = await artistsRes.json();
  const topArtists  = (artistsData.items || []).map(a => a.name).slice(0, 8);

  // Fetch top genres from artists
  const allGenres = (artistsData.items || []).flatMap(a => a.genres || []);
  const genreCount = {};
  allGenres.forEach(g => { genreCount[g] = (genreCount[g] || 0) + 1; });
  const topGenres = Object.entries(genreCount)
    .sort(([,a],[,b]) => b - a)
    .slice(0, 5)
    .map(([g]) => g);

  const spotifyData = {
    connected:    true,
    topArtists,
    topGenres,
    connectedAt:  new Date().toISOString(),
    // Don't store access token in DB — it expires and we don't need persistence
  };

  // Save to Supabase user_profiles
  try {
    const { supabase } = await import('../utils/supabase');
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('user_profiles')
        .update({ spotify_data: spotifyData })
        .eq('user_id', user.id);
    }
  } catch {}

  return { spotifyData, error: null };
}

// ── Disconnect Spotify ────────────────────────────────────────────────────
export async function disconnectSpotify() {
  try {
    const { supabase } = await import('../utils/supabase');
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('user_profiles')
        .update({ spotify_data: null })
        .eq('user_id', user.id);
    }
  } catch {}
}

// ── Spotify icon ──────────────────────────────────────────────────────────
function SpotifyIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#1DB954">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
    </svg>
  );
}

// ── SpotifyConnectButton component ────────────────────────────────────────
export function SpotifyConnectButton({ compact }) {
  const tr = useTr();
  const { currentUser, setCurrentUser } = useApp();
  const [connecting, setConnecting] = useState(false);
  const [error,      setError]      = useState('');

  const hasSpotify = !!currentUser?.spotify?.connected;

  const connect = async () => {
    if (!SPOTIFY_CLIENT_ID) {
      setError('Spotify not configured — add REACT_APP_SPOTIFY_CLIENT_ID');
      return;
    }
    setConnecting(true);
    await initiateSpotifyOAuth(); // redirects away
  };

  const disconnect = async () => {
    await disconnectSpotify();
    setCurrentUser(u => ({ ...u, spotify: null }));
  };

  if (compact) return hasSpotify ? (
    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.3rem 0.6rem', background:'rgba(29,185,84,0.1)', border:'1px solid rgba(29,185,84,0.3)', borderRadius:'99px' }}>
      <SpotifyIcon size={12} />
      <span style={{ fontSize:'0.62rem', color:'#1DB954', fontFamily:'Outfit', fontWeight:600 }}>{tr('spotify.connected')}</span>
      <button onClick={disconnect} style={{ background:'none', border:'none', color:'rgba(29,185,84,0.6)', cursor:'pointer', fontSize:'0.7rem', lineHeight:1 }}>✕</button>
    </div>
  ) : (
    <button onClick={connect} disabled={connecting} style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.3rem 0.65rem', background:connecting?'rgba(29,185,84,0.05)':'rgba(29,185,84,0.1)', border:'1px solid rgba(29,185,84,0.3)', borderRadius:'99px', cursor:connecting?'default':'pointer', color:'#1DB954', fontFamily:'Outfit', fontSize:'0.62rem', fontWeight:600 }}>
      <SpotifyIcon size={12}/>
      {connecting ? tr('spotify.connecting') : tr('spotify.connect')}
    </button>
  );

  return (
    <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem 1.1rem', marginBottom:'1rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.7rem', marginBottom: hasSpotify ? '0.8rem' : 0 }}>
        <SpotifyIcon size={24}/>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:'0.8rem', fontWeight:600, color:'var(--white)', fontFamily:'Outfit' }}>Spotify</div>
          <div style={{ fontSize:'0.63rem', color:'var(--muted)' }}>
            {hasSpotify
              ? `Connected · ${currentUser?.spotify?.topArtists?.length || 0} top artists`
              : tr('spotify.sub') || 'Share your music taste'}
          </div>
        </div>
        {hasSpotify ? (
          <button onClick={disconnect} style={{ padding:'0.3rem 0.65rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'99px', color:'var(--muted)', cursor:'pointer', fontFamily:'Outfit', fontSize:'0.62rem' }}>{tr('spotify.disconnect')}</button>
        ) : (
          <button onClick={connect} disabled={connecting} style={{ padding:'0.35rem 0.8rem', background:'#1DB954', border:'none', borderRadius:'99px', color:'#fff', fontFamily:'Outfit', fontSize:'0.68rem', fontWeight:700, cursor:connecting?'default':'pointer' }}>
            {connecting ? 'Redirecting…' : 'Connect'}
          </button>
        )}
      </div>
      {error && <p style={{ fontSize:'0.7rem', color:'var(--red,#E07070)', marginTop:'0.4rem' }}>{error}</p>}
      {hasSpotify && currentUser?.spotify?.topArtists?.length > 0 && (
        <div>
          <p style={{ fontSize:'0.6rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.4rem' }}>Top artists</p>
          <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
            {currentUser?.spotify?.topArtists?.map(a => (
              <span key={a} style={{ fontSize:'0.68rem', padding:'0.18rem 0.55rem', background:'rgba(29,185,84,0.1)', border:'1px solid rgba(29,185,84,0.25)', borderRadius:'99px', color:'#1DB954', fontFamily:'Outfit' }}>{a}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function SpotifyBadge({ user }) {
  if (!user?.spotify?.topArtists?.length) return null;
  return (
    <div style={{ marginTop:'0.8rem' }}>
      <p style={{ fontSize:'0.58rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.4rem', display:'flex', alignItems:'center', gap:'0.35rem' }}>
        <SpotifyIcon size={11}/> Music
      </p>
      <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap' }}>
        {user.spotify.topArtists.slice(0, 4).map(a => (
          <span key={a} style={{ fontSize:'0.65rem', padding:'0.15rem 0.5rem', background:'rgba(29,185,84,0.08)', border:'1px solid rgba(29,185,84,0.2)', borderRadius:'99px', color:'#1DB954', fontFamily:'Outfit' }}>{a}</span>
        ))}
      </div>
    </div>
  );
}

export default SpotifyConnectButton;
